import { ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useAuth } from '../../../../contexts/authContext';
import { projectDataClient } from '../../../../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../../../../lib/uploadAttachment';
import { libraryReleaseClient } from '../../../../pages/Library/reuseClient';
export const DOCUMENTS_KEY = 'proposal-documents';
/** Plain-language text for a failed write; the server's own message otherwise. */ export function documentErrorText(err) {
    const raw = err instanceof ConnectError ? err.rawMessage : String(err);
    if (/filed in another project/i.test(raw)) {
        return 'This file is already filed to another proposal, so it cannot be brought in here.';
    }
    if (/archive/i.test(raw)) {
        return 'Archives cannot be brought in. Import the individual files instead.';
    }
    return raw || 'Something went wrong. Try again.';
}
export function useProposalDocuments(projectId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            DOCUMENTS_KEY,
            `${user?.tenant_id}:${user?.id}`,
            projectId
        ],
        enabled: Boolean(projectId),
        staleTime: 30000,
        retry: false,
        queryFn: ()=>projectDataClient.listProposalDocuments({
                projectId
            })
    });
}
/**
 * One request at a time per action: a second click while one is in flight is dropped (the ref
 * flips synchronously, before React has re-rendered the disabled button). Settling refetches the
 * list and the sidebar count.
 */ function useGuardedMutation(fn, refresh) {
    const inflight = useRef(false);
    const mutation = useMutation({
        mutationFn: fn,
        onSettled: async ()=>{
            inflight.current = false;
            await refresh();
        }
    });
    const mutate = (vars, options)=>{
        if (inflight.current) return;
        inflight.current = true;
        mutation.mutate(vars, options);
    };
    return {
        ...mutation,
        mutate
    };
}
export function useProposalDocumentWrites(projectId) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const refresh = async ()=>{
        await Promise.all([
            qc.invalidateQueries({
                queryKey: [
                    DOCUMENTS_KEY
                ]
            }),
            qc.invalidateQueries({
                queryKey: [
                    'proposal-record-header'
                ]
            })
        ]);
    };
    return {
        update: useGuardedMutation((a)=>projectDataClient.updateProposalDocument({
                projectId,
                ...a
            }), refresh),
        remove: useGuardedMutation((documentId)=>projectDataClient.removeProposalDocument({
                projectId,
                documentId
            }), refresh),
        bringIn: useGuardedMutation((a)=>projectDataClient.bringPursuitDocumentsIntoProposal({
                projectId,
                ...a
            }), refresh),
        setPackage: useGuardedMutation((a)=>projectDataClient.updateVolumeDocument({
                projectId,
                ...a
            }), refresh),
        upload: useGuardedMutation(async (a)=>{
            const uploaded = await uploadAttachmentViaTesoro(a.file, {
                tenantId: user?.tenant_id ?? '',
                ownerUserId: user?.id ?? '',
                projectId,
                autoProcess: false
            });
            return projectDataClient.uploadProjectFiles({
                projectId,
                artifactIds: [
                    uploaded.artifactId
                ],
                hubCategory: a.category,
                sensitivity: a.sensitivity
            });
        }, refresh),
        libraryImport: useGuardedMutation(async (a)=>{
            const adopted = await libraryReleaseClient.createLibraryReuse({
                releaseId: a.releaseId,
                transferMode: 'adopted',
                receivingObjectType: 'proposal',
                receivingObjectId: projectId
            });
            return projectDataClient.uploadProjectFiles({
                projectId,
                artifactIds: a.artifactIds,
                hubCategory: a.category,
                libraryReuseId: adopted.reuse?.reuseId ?? ''
            });
        }, refresh)
    };
}
export function useSelectableReleases(enabled) {
    return useQuery({
        queryKey: [
            'proposal-documents-releases'
        ],
        enabled,
        gcTime: 0,
        retry: false,
        queryFn: async ()=>(await libraryReleaseClient.listSelectableReleases({})).releases
    });
}
/** Current direct file references of one Library item: no pinned versions, no archives. */ export function useLibraryReferences(libraryItemId) {
    return useQuery({
        queryKey: [
            'proposal-documents-library-refs',
            libraryItemId
        ],
        enabled: libraryItemId !== '',
        gcTime: 0,
        retry: false,
        queryFn: async ()=>{
            const item = await projectDataClient.getLibraryItem({
                id: libraryItemId
            });
            const all = item.references.filter((r)=>r.referenceKind === 'artifact');
            const direct = all.filter((r)=>r.artifactVersionId === '' && !/\.(zip|tar|gz|7z)$/i.test(r.label));
            return {
                direct,
                hidden: all.length - direct.length
            };
        }
    });
}
